import { useState } from "react";
import { Mail, MapPin, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/context/LanguageContext";
import { companyConfig, getSiteContent } from "@/data/siteContent";
import { submitContactRequest } from "@/lib/api";

const initialFormState = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  service_interest: "",
  preferred_contact: "phone",
  message: "",
};

export default function ContactPage() {
  const { language, isArabic } = useLanguage();
  const content = getSiteContent(language);
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContactRequest(formData);
      toast.success(content.contact.success);
      setFormData(initialFormState);
    } catch (_error) {
      toast.error(content.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-white py-16 md:py-24" data-testid="contact-page">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-3xl text-center" data-testid="contact-header-block">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="contact-page-title">
            {content.contact.title}
          </h1>
          <p className="mt-4 text-base text-slate-600 md:text-lg" data-testid="contact-page-subtitle">
            {content.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="contact-layout-grid">
          <Card className="border-slate-200" data-testid="contact-form-card">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-request-form">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder={content.contact.form.fullName}
                    data-testid="contact-form-full-name-input"
                  />
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    placeholder={content.contact.form.companyName}
                    data-testid="contact-form-company-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={content.contact.form.email}
                    data-testid="contact-form-email-input"
                  />
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder={content.contact.form.phone}
                    data-testid="contact-form-phone-input"
                  />
                </div>
                <Input
                  name="service_interest"
                  value={formData.service_interest}
                  onChange={handleChange}
                  required
                  placeholder={content.contact.form.serviceInterest}
                  data-testid="contact-form-service-interest-input"
                />
                <div className="space-y-2" data-testid="contact-form-preferred-contact-group">
                  <p className="text-sm font-medium text-slate-700" data-testid="contact-form-preferred-contact-label">
                    {content.contact.form.preferredContact}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {content.contact.preferredMethods.map((method) => (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, preferred_contact: method.value }))}
                        className={`cursor-pointer rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-300 ${
                          formData.preferred_contact === method.value
                            ? "border-secondary bg-secondary/10 text-secondary"
                            : "border-slate-300 text-slate-600 hover:bg-slate-100"
                        }`}
                        data-testid={`contact-form-preferred-contact-${method.value}-button`}
                      >
                        {method.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder={content.contact.form.message}
                  data-testid="contact-form-message-input"
                />
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  className="h-11 w-full cursor-pointer whitespace-nowrap md:h-12"
                  data-testid="contact-form-submit-button"
                >
                  {isSubmitting ? content.contact.form.submitting : content.contact.form.submit}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-4" data-testid="contact-info-block">
            <Card className="border-slate-200" data-testid="contact-phone-card">
              <CardContent className="flex items-center gap-3 p-5">
                <PhoneCall className="h-5 w-5 text-secondary" />
                <div>
                  <p className="text-sm text-slate-500" data-testid="contact-phone-label">
                    {isArabic ? "الهاتف" : "Phone"}
                  </p>
                  <a href={`tel:${companyConfig.phone}`} className="font-semibold text-slate-900" data-testid="contact-phone-value-link">
                    {companyConfig.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="contact-email-card">
              <CardContent className="flex items-center gap-3 p-5">
                <Mail className="h-5 w-5 text-secondary" />
                <div>
                  <p className="text-sm text-slate-500" data-testid="contact-email-label">
                    {isArabic ? "البريد الإلكتروني" : "Email"}
                  </p>
                  <a href={`mailto:${companyConfig.email}`} className="font-semibold text-slate-900" data-testid="contact-email-value-link">
                    {companyConfig.email}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="contact-whatsapp-card">
              <CardContent className="flex items-center gap-3 p-5">
                <MessageCircle className="h-5 w-5 text-secondary" />
                <div>
                  <p className="text-sm text-slate-500" data-testid="contact-whatsapp-label">
                    WhatsApp
                  </p>
                  <a
                    href={companyConfig.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-slate-900"
                    data-testid="contact-whatsapp-value-link"
                  >
                    {isArabic ? "محادثة مباشرة" : "Direct Chat"}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="contact-map-card">
              <CardContent className="space-y-3 p-5">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-secondary" />
                  <p className="font-semibold text-slate-900" data-testid="contact-map-title">
                    {isArabic ? "الموقع" : "Location"}
                  </p>
                </div>
                <a
                  href={companyConfig.mapLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 items-center justify-center rounded-md bg-slate-900 px-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-slate-800"
                  data-testid="contact-map-open-link"
                >
                  {isArabic ? "فتح على الخريطة" : "Open Map"}
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
